export const LOCALES = ['fa', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_DIRECTION: Record<Locale, 'rtl' | 'ltr'> = { fa: 'rtl', en: 'ltr' };
export const LOCALE_LABEL: Record<Locale, string> = { fa: 'فارسی', en: 'English' };

export type Messages = Record<string, string>;

export const en: Messages = {
  'app.name': 'Jira for Telegram',

  'common.yes': 'Yes',
  'common.no': 'No',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.close': 'Close',
  'common.next': 'Next',
  'common.previous': 'Previous',
  'common.done': 'Done',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.search': 'Search',
  'common.loading': 'Loading…',
  'common.none': 'None',
  'common.unassigned': 'Unassigned',
  'common.more': 'More',
  'common.refresh': 'Refresh',
  'common.skip': 'Skip',
  'common.open_in_jira': 'Open in Jira',
  'common.open_app': 'Open app',
  'common.page': 'Page {page}',
  'common.showing': 'Showing {from}–{to} of {total}',
  'common.nothing_found': 'Nothing found.',
  'common.enabled': 'On',
  'common.disabled': 'Off',
  'common.locked': 'Locked by an admin',

  'start.welcome':
    '<b>Jira for Telegram</b>\n\nConnect your Jira account and get your work done right here.\n\nEverything you do is recorded in Jira under your own name, and you get exactly the access you already have there. Not a bit more.',
  'start.connected': 'Connected as <b>{name}</b> on <b>{site}</b>.',
  'start.not_connected': 'You have not connected a Jira account yet.',
  'start.not_connected_hint':
    'You are not connected yet. Send /connect and I will walk you through it.',
  'start.connect_button': 'Connect Jira',
  'start.help_hint': 'Send /help to see what I can do.',

  'connect.prompt':
    'Tap below to sign in with your own Jira account.\n\nYour password never reaches the bot, and the bot never acts through anyone else’s account.',
  'connect.button': 'Authorise with Jira',
  'connect.expires': 'This link expires in {minutes} minutes.',
  'connect.success': '✅ Connected as <b>{name}</b>.',
  'connect.pick_site': 'You have access to several Jira sites. Choose the one to work in:',
  'connect.site_selected': 'Active site: <b>{site}</b>',
  'connect.reauth_needed':
    '⚠️ Your Jira connection expired and must be renewed. Tap below to reconnect.',
  'connect.disconnected': 'Your Jira connection has been removed and the stored tokens deleted.',
  'connect.disconnect_confirm':
    'Disconnect your Jira account? Your stored tokens will be deleted. Your Jira data is not affected.',
  'connect.dc_prompt':
    'Your Jira is a Data Center install, so instead of a normal sign-in it needs a <b>personal access token</b>.\n\nSend the token to me in a message. It gets encrypted and stays with your account only.\n\nIf you do not have one yet, create it here:\n{url}',
  'connect.dc_stored': '✅ Connected as <b>{name}</b> using your own personal access token.',

  'home.title': '<b>My work</b>',
  'home.assigned': 'Assigned to me',
  'home.reported': 'Reported by me',
  'home.watched': 'Watched',
  'home.due_soon': 'Due soon',
  'home.overdue': 'Overdue',
  'home.sprint': 'My sprint work',
  'home.approvals': 'My approvals',
  'home.recent': 'Recently viewed',
  'home.empty': 'Nothing here right now.',
  'home.counts': '{assigned} assigned · {overdue} overdue · {dueSoon} due soon',

  'issue.status': 'Status',
  'issue.assignee': 'Assignee',
  'issue.reporter': 'Reporter',
  'issue.priority': 'Priority',
  'issue.type': 'Type',
  'issue.due': 'Due',
  'issue.updated': 'Updated',
  'issue.created': 'Created',
  'issue.sprint': 'Sprint',
  'issue.epic': 'Epic',
  'issue.parent': 'Parent',
  'issue.labels': 'Labels',
  'issue.resolution': 'Resolution',
  'issue.time': 'Time',
  'issue.not_found': 'That issue does not exist, or your Jira account cannot see it.',

  'action.open': 'Open',
  'action.assign': 'Assign',
  'action.assign_to_me': 'Assign to me',
  'action.unassign': 'Unassign',
  'action.transition': 'Move',
  'action.comment': 'Comment',
  'action.log_work': 'Log work',
  'action.watch': 'Watch',
  'action.unwatch': 'Unwatch',
  'action.link': 'Link',
  'action.attach': 'Attach',
  'action.edit': 'Edit',
  'action.delete': 'Delete',
  'action.more': 'More…',
  'action.vote': 'Vote',
  'action.unvote': 'Remove vote',
  'action.mute': 'Mute this issue',
  'action.unmute': 'Unmute',

  'assign.pick': 'Who should this be assigned to?',
  'assign.me': 'Me',
  'assign.search': 'Search Jira users',
  'assign.teammates': 'Linked teammates',
  'assign.others': 'Other assignable users',
  'assign.none_assignable': 'Jira does not offer anyone to assign this issue to.',
  'assign.done': '✅ {issue} assigned to <b>{name}</b>.',
  'assign.done_unlinked':
    '✅ {issue} assigned to <b>{name}</b> in Jira. They have not connected Telegram yet, so nothing was sent to them.',
  'assign.cleared': '✅ {issue} is now unassigned.',
  'assign.not_assignable': 'Jira will not let this issue go to that person.',

  'transition.pick': 'Move <b>{issue}</b> to:',
  'transition.none': 'Jira is not offering you any status change on this issue right now.',
  'transition.done': '✅ {issue} moved to <b>{status}</b>.',
  'transition.needs_fields': 'This status change needs more information. Opening the app…',

  'comment.prompt': 'Send the comment text for <b>{issue}</b>.',
  'comment.added': '✅ Comment added to {issue}.',
  'comment.empty': 'A comment cannot be empty.',
  'comment.restricted': 'Restricted to {role}',

  'worklog.prompt': 'How much time did you spend on <b>{issue}</b>?',
  'worklog.custom': 'Custom…',
  'worklog.custom_prompt': 'Send the time, for example <code>1h 30m</code>.',
  'worklog.added': '✅ Logged {time} on {issue}.',
  'worklog.invalid': 'I could not read that duration. Try something like <code>1h 30m</code>.',

  'search.prompt': 'Send a search term or a JQL query.',
  'search.results': 'Found {count} issue(s).',
  'search.no_results': 'Nothing found.',
  'search.invalid_jql': 'Jira did not understand that query.',
  'search.saved': 'Saved as <b>{name}</b>.',

  'create.pick_project': 'Which project?',
  'create.pick_type': 'Which issue type?',
  'create.summary_prompt': 'Send a short summary for the new issue.',
  'create.description_prompt': 'Send a description, or tap Skip.',
  'create.created': '✅ Created <b>{issue}</b>.',
  'create.missing_fields':
    'This issue type needs fields the bot cannot show. Open the app to fill them in.',

  'sites.title': 'Your Jira sites',
  'sites.active': 'Active',
  'sites.switch': 'Switch to {site}',
  'sites.switched': 'Active site is now <b>{site}</b>.',
  'sites.only_one': 'You have access to one Jira site: <b>{site}</b>.',

  'access.title': '<b>My access</b>',
  'access.identity': 'Connected as <b>{name}</b>{email}',
  'access.site': 'Site: <b>{site}</b>',
  'access.token_health': 'Connection: {status}',
  'access.scopes': 'Granted scopes ({count})',
  'access.projects': 'Accessible projects ({count})',
  'access.roles': 'Project roles',
  'access.refresh': 'Refresh access',
  'access.refreshed': 'Access information refreshed from Jira.',
  'access.explain_scopes':
    'Two things decide what you can do here. First, OAuth scopes: what this app is allowed to ask Jira for. Second, your own Jira permissions, which have the final say.\n\nBot policy can only narrow that down. It never widens it.',

  'notify.title': '<b>Notifications</b>',
  'notify.master': 'All notifications',
  'notify.mode': 'Delivery',
  'notify.mode_immediate': 'Immediate',
  'notify.mode_digest': 'Digest',
  'notify.mode_off': 'Off',
  'notify.quiet_hours': 'Quiet hours',
  'notify.quiet_window': '{start} – {end} ({tz})',
  'notify.snooze': 'Snooze',
  'notify.snoozed_until': 'Snoozed until {time}.',
  'notify.projects': 'Projects',
  'notify.muted_issues': 'Muted issues',
  'notify.events': 'Events',
  'notify.locked_by_admin': 'An administrator locked this one.',
  'notify.saved': 'Saved.',
  'notify.not_realtime':
    'This event is not real-time: Jira publishes no webhook for it, so it is detected by a periodic check.',
  'notify.mandatory': 'Required by your administrator ({name}). You cannot turn this one off.',
  'notify.mandatory_title': 'Required notifications',
  'notify.mandatory_hint':
    'Your administrator sends these to you whatever your settings below say. They still only ever show you issues you can already open in Jira.',
  'notify.mandatory_via_all': 'Everyone',
  'notify.mandatory_via_team': 'Your team',
  'notify.mandatory_via_direct': 'You',
  'notify.my_teams': 'My teams',

  'settings.title': '<b>Settings</b>',
  'settings.language': 'Language',
  'settings.timezone': 'Timezone',
  'settings.site': 'Active Jira site',
  'settings.disconnect': 'Disconnect Jira',
  'settings.language_set': 'Language set to {language}.',
  'settings.timezone_prompt': 'Send your timezone, for example <code>Asia/Tehran</code>.',
  'settings.timezone_set': 'Timezone set to {timezone}.',
  'settings.timezone_invalid': 'I do not know that timezone.',

  'admin.title': '<b>Administration</b>',
  'admin.users': 'Users',
  'admin.features': 'Feature policies',
  'admin.notifications': 'Notification policies',
  'admin.audit': 'Audit log',
  'admin.health': 'Health',
  'admin.no_access': 'You do not administer this workspace.',
  'admin.token_never_shown':
    'Token values are never shown here. Administrators can see connection health only.',

  'admin.teams': 'Teams',
  'admin.teams_hint':
    'A team is a name for a group of people. Give it a Jira group name and its membership is read from Jira; leave that empty and you keep the list here.',
  'admin.team_new': 'New team',
  'admin.team_name': 'Team name',
  'admin.team_jira_group': 'Jira group',
  'admin.team_jira_group_none': 'Kept here, not from Jira',
  'admin.team_sync': 'Sync from Jira',
  'admin.team_synced': 'Last synced {when}',
  'admin.team_never_synced': 'Never synced',
  'admin.team_members': '{count} members',
  'admin.team_add_member': 'Add someone',
  'admin.team_sync_note':
    'Everyone is asked which Jira groups they are in using their own account, so people who have not connected Jira must be added by hand.',

  'admin.mandates': 'Required notifications',
  'admin.mandates_hint':
    'A required notification is delivered whatever anyone has switched off — the recipient, or you — and even for a change they made themselves. The only two things that stop one are a missing product and Jira: everybody still only sees issues they can already open.',
  'admin.mandate_new': 'New requirement',
  'admin.mandate_name': 'Name',
  'admin.mandate_events': 'Force these events',
  'admin.mandate_projects': 'In these projects',
  'admin.mandate_targets': 'For whom',
  'admin.mandate_all_members': 'Everyone in the workspace',
  'admin.mandate_teams': 'Teams',
  'admin.mandate_people': 'Individual people',
  'admin.mandate_bypass_quiet': 'Also during their quiet hours',
  'admin.mandate_bypass_quiet_hint': 'On by default. Turn it off for something that matters but can wait for morning.',
  'admin.mandate_reaches': 'Reaches {count} people',
  'admin.mandate_nobody': 'Reaches nobody — choose a target',

  'admin.broadcast': 'Send a message',
  'admin.broadcast_hint':
    'Sends a plain Telegram message. It is not a Jira notification and shows nobody anything about Jira.',
  'admin.broadcast_text': 'Message',
  'admin.broadcast_send': 'Send',
  'admin.broadcast_audience': 'Send to',
  'admin.broadcast_audience_all': 'Everyone who started the bot',
  'admin.broadcast_audience_selected': 'People I pick',
  'admin.broadcast_audience_team': 'A team',
  'admin.broadcast_history': 'Sent before',
  'admin.broadcast_queued': 'Queued for {count} people.',
  'admin.broadcast_progress': '{sent} sent · {failed} failed of {total}',
  'admin.broadcast_cancel': 'Stop sending',
  'admin.broadcast_select_all': 'Select all shown',
  'admin.broadcast_clear': 'Clear selection',
  'admin.broadcast_selected': '{count} selected',
  'admin.audience_search': 'Search people',
  'admin.audience_linked': 'Jira connected',
  'admin.audience_not_linked': 'Not connected',
  'admin.audience_started': 'Started {when}',
  'admin.audience_total': '{count} people have started the bot',

  'confirm.title': '⚠️ <b>Confirm</b>',
  'confirm.destructive': 'This cannot be undone.',
  'confirm.target': 'Target: <b>{target}</b>',
  'confirm.effect': 'Effect: {effect}',
  'confirm.expires': 'This confirmation expires in {seconds} seconds.',
  'confirm.yes': 'Yes, do it',
  'confirm.expired': 'That confirmation has expired. Please start again.',
  'confirm.mismatch': 'That confirmation does not match this action.',

  'error.NotConnected': 'Connect your Jira account first.',
  'error.ReauthRequired': 'Your Jira connection expired. Please reconnect.',
  'error.SiteNotSelected': 'Choose a Jira site first.',
  'error.JiraUnauthorized': 'Jira rejected your session. Please reconnect.',
  'error.JiraForbidden': 'Jira did not allow this action for your connected account.',
  'error.JiraNotFound': 'That item does not exist, or your Jira account cannot see it.',
  'error.JiraValidationError': 'Jira rejected this request: {detail}',
  'error.JiraRateLimited': 'Jira is busy right now. Try again in a few seconds.',
  'error.JiraUnavailable': 'Jira is temporarily unavailable. Please try again.',
  'error.JiraConflict': 'Someone changed this at the same time. Reload and try again.',
  'error.BotPolicyDenied': 'Your workspace policy does not allow this.',
  'error.FeatureDisabled': 'This feature is turned off for your workspace.',
  'error.InvalidCallback': 'That button is no longer valid.',
  'error.ExpiredCallback': 'That button has expired. Please reopen the item.',
  'error.InvalidMiniAppSession': 'Your session is invalid. Please reopen the app.',
  'error.ProviderUnsupported': 'This feature is only available on Jira Cloud.',
  'error.AttachmentTooLarge': 'That file is too large.',
  'error.Internal': 'Something went wrong. Please try again.',
  'error.generic': 'Something went wrong. Please try again.',
  'error.refresh_access': 'Refresh access',
  'error.reconnect': 'Reconnect Jira',
  'error.not_allowlisted': 'This bot is still in testing and only a few accounts can use it.',

  'event.issue.assigned_to_me': '📌 <b>{issue}</b> was assigned to you',
  'event.issue.unassigned': '📭 <b>{issue}</b> was unassigned',
  'event.issue.reassigned': '🔄 <b>{issue}</b> was reassigned to {to}',
  'event.issue.status_changed': '🔀 <b>{issue}</b> moved to <b>{to}</b>',
  'event.issue.created': '🆕 <b>{issue}</b> was created',
  'event.issue.updated': '✏️ <b>{issue}</b> was updated',
  'event.issue.deleted': '🗑 <b>{issue}</b> was deleted',
  'event.issue.priority_changed': '❗ <b>{issue}</b> priority is now {to}',
  'event.issue.resolution_changed': '✅ <b>{issue}</b> was resolved as {to}',
  'event.issue.due_date_changed': '📅 <b>{issue}</b> is now due {to}',
  'event.issue.due_soon': '⏰ <b>{issue}</b> is due {due}',
  'event.issue.overdue': '🔴 <b>{issue}</b> is overdue since {due}',
  'event.comment.created': '💬 {actor} commented on <b>{issue}</b>',
  'event.comment.updated': '💬 {actor} edited a comment on <b>{issue}</b>',
  'event.comment.deleted': '💬 A comment on <b>{issue}</b> was deleted',
  'event.comment.mentioned_me': '📣 {actor} mentioned you on <b>{issue}</b>',
  'event.attachment.added': '📎 A file was attached to <b>{issue}</b>',
  'event.attachment.removed': '📎 A file was removed from <b>{issue}</b>',
  'event.link.added': '🔗 <b>{issue}</b> was linked',
  'event.link.removed': '🔗 A link was removed from <b>{issue}</b>',
  'event.worklog.added': '⏱ Work was logged on <b>{issue}</b>',
  'event.sprint.started': '🏁 Sprint <b>{sprint}</b> started',
  'event.sprint.closed': '🏁 Sprint <b>{sprint}</b> was completed',
  'event.issue.added_to_sprint': '🏃 <b>{issue}</b> was added to {to}',
  'event.jsm.approval_required': '🖊 <b>{issue}</b> is waiting for your approval',
  'event.jsm.sla_warning': '⏳ SLA warning on <b>{issue}</b>',
  'event.jsm.sla_breached': '🚨 SLA breached on <b>{issue}</b>',
  'event.jsm.public_comment': '💬 New reply on <b>{issue}</b>',
  'event.digest.title': '<b>Your Jira digest</b> · {count} update(s)',
  'event.by': 'by {actor}',

  'help.title': '<b>What I can do</b>',
  'help.commands': 'Commands',
  'help.security':
    'Everything here runs as your own Jira account. If you cannot see something in Jira, you cannot see it here either, and if you cannot do something there, you cannot do it here.',
  'issue.overview': 'Overview',
  'issue.description': 'Description',
  'issue.no_description': 'No description was added to this issue.',
  'issue.components': 'Components',
  'issue.fix_versions': 'Fix versions',
  'issue.story_points': 'Story points',
  'issue.subtasks': 'Subtasks',
  'issue.comments': 'Comments',
  'issue.worklogs': 'Work log',
  'issue.history': 'History',
  'issue.no_comments': 'No comments yet.',
  'issue.no_worklogs': 'No time logged yet.',
  'issue.no_history': 'No changes recorded yet.',
  'issue.watchers': 'Watchers',
  'issue.votes': 'Votes',
  'issue.project': 'Project',
  'issue.details': 'Details',
  'home.scope': 'Showing',
  'home.scope_mine': 'My work',
  'home.scope_all': 'Everything I can see',
  'home.nothing_assigned': 'Nothing is assigned to you right now.',
  'home.nothing_assigned_hint': 'Switch to “Everything I can see” to browse the whole site, or pick a project below.',
  'home.browse_projects': 'Browse a project',
  'home.open_issues': 'Open issues',
  'home.unassigned': 'Unassigned',
  'home.all_open': 'All open',
  'home.pick_project': 'Pick a project',
  'home.no_projects': 'You cannot browse any project on this site.',
  'notify.cadence': 'How often',
  'notify.cadence_hourly': 'Every hour',
  'notify.cadence_4h': 'Every 4 hours',
  'notify.cadence_daily': 'Once a day',
  'notify.cadence_weekly': 'Once a week',
  'notify.scope': 'What counts as mine',
  'notify.scope_hint':
    'These narrow things down rather than add to them: switch one on and you stop hearing about anything else. Being mentioned by name always reaches you regardless.',
  'notify.suppress_self': 'Skip things I did myself',
  'notify.quiet_from': 'From',
  'notify.quiet_to': 'Until',
  'notify.rules': 'My rules',
  'notify.rules_hint': 'A rule sends you a notification when something matches it. Without any rule you get the defaults below.',
  'notify.rule_new': 'New rule',
  'notify.rule_name': 'Rule name',
  'notify.rule_events': 'When this happens',
  'notify.rule_projects': 'In these projects',
  'notify.rule_issue_types': 'For these issue types',
  'notify.rule_statuses': 'While the status is',
  'notify.rule_assignee': 'And the assignee is',
  'notify.rule_any': 'Any',
  'notify.rule_all_projects': 'All projects',
  'notify.rule_me': 'Me',
  'notify.rule_group': 'Anyone in a team',
  'notify.rule_pick_groups': 'Which teams',
  'notify.rule_anyone': 'Anyone',
  'notify.rule_unassigned': 'Nobody (unassigned)',
  'notify.rule_delete': 'Delete rule',
  'notify.rule_saved': 'Rule saved.',
  'notify.rule_none': 'You have no rules yet.',
  'notify.rule_stale': 'Also warn me when nothing changes for',
  'notify.rule_stale_off': 'Do not warn',
  'notify.rule_hours': '{hours} hours',
  'notify.rule_days': '{days} days',
  'notify.rule_summary_all': 'Everything, everywhere',
  'notify.defaults': 'Default events',
  'notify.advanced': 'Advanced',
  'admin.identity': 'Identity',
  'admin.account': 'Account',
  'admin.provider': 'Provider',
  'admin.notifications_allowed': 'Notifications allowed',
  'admin.lock_user_settings': 'Lock user settings',
  'admin.permission_snapshots': 'Permission snapshots',
  'admin.tenant_policy': 'Workspace policy',
  'admin.notifications_enabled': 'Notifications enabled',
  'admin.webhooks': 'Webhooks',
  'admin.registrations': 'Registrations',
  'admin.backlog': 'Backlog',
  'admin.dead_letters': 'Dead letters',
  'admin.suppressed': 'Suppressed',
  'admin.queues': 'Queues',
  'admin.needs_reconnect': 'Needs reconnect',
  'agile.title': 'Boards',
  'assets.title': 'Assets',
  'assets.object_types': 'Object types',
  'power.title': 'Power tools',
  'common.required': 'Required',
  'common.optional': 'Optional',
  'common.all': 'All',
  'common.clear': 'Clear',
  'common.apply': 'Apply',
  'common.add': 'Add',
  'common.fields_count': '{count} fields',
  'event.issue.stale': 'No change on {issue} for {hours} hours',
  'search.box_label': 'Search',
  'search.box_hint': 'An issue key, or any words from the title.',
  'search.advanced': 'Advanced search',
  'search.advanced_hint': 'JQL is the query language Jira itself uses for filters. Leave this closed unless you already write it.',
  'search.showing_for': 'Showing results for',
  'search.edit_query': 'Edit this query',
  'agile.board': 'Board',
  'agile.backlog': 'Backlog',
  'agile.sprints': 'Sprints',
  'agile.epics': 'Epics',
  'agile.pick_board': 'Pick a board',
  'agile.no_boards': 'You cannot see any board on this site.',
  'agile.kanban_no_backlog': 'A kanban board has no separate backlog.',
  'agile.kanban_no_sprints': 'A kanban board does not run sprints.',
  'agile.board_empty': 'This board has no issues right now.',
  'agile.scrum': 'Scrum',
  'agile.kanban': 'Kanban',
  'agile.no_active_sprint': 'This board has no active sprint right now.',
  'agile.sprint_now': 'Active sprint',
  'agile.unmapped': 'Not on any column',
  'agile.column_empty': 'Empty',
  'agile.issues_count': '{count} issues',
  'sprint.active': 'Active',
  'sprint.future': 'Not started',
  'sprint.closed': 'Closed',
  'epic.done': 'Done',
  'epic.open': 'Open',
  'agile.no_active_sprint_hint': 'Nothing is running on this board at the moment. These are its sprints:',
  'agile.sprint_none': 'This board has no sprints yet.',
  'agile.truncated': 'Showing the first {shown} of {total} issues on this board.',
};

export const fa: Messages = {
  'app.name': 'جیرا در تلگرام',

  'common.yes': 'بله',
  'common.no': 'خیر',
  'common.back': 'بازگشت',
  'common.cancel': 'انصراف',
  'common.confirm': 'تأیید',
  'common.close': 'بستن',
  'common.next': 'بعدی',
  'common.previous': 'قبلی',
  'common.done': 'انجام شد',
  'common.save': 'ذخیره',
  'common.delete': 'حذف',
  'common.edit': 'ویرایش',
  'common.search': 'جستجو',
  'common.loading': 'یه لحظه…',
  'common.none': 'چیزی نیست',
  'common.unassigned': 'بدون مسئول',
  'common.more': 'بیشتر',
  'common.refresh': 'به‌روزرسانی',
  'common.skip': 'رد کردن',
  'common.open_in_jira': 'باز کردن در جیرا',
  'common.open_app': 'باز کردن برنامه',
  'common.page': 'صفحهٔ {page}',
  'common.showing': 'نمایش {from}–{to} از {total}',
  'common.nothing_found': 'چیزی پیدا نشد.',
  'common.enabled': 'روشن',
  'common.disabled': 'خاموش',
  'common.locked': 'مدیر قفلش کرده',

  'start.welcome': '<b>جیرا در تلگرام</b>\n\nحساب جیراتون رو وصل کنید تا کارهاتون رو از همین‌جا جلو ببرید.\n\nهر چی اینجا انجام بدید، تو جیرا به اسم خودتون ثبت می‌شه. دسترسی‌هاتون هم دقیقاً همونیه که تو جیرا دارید، نه یه ذره بیشتر.',
  'start.connected': 'با حساب <b>{name}</b> روی <b>{site}</b> وصلید.',
  'start.not_connected': 'هنوز حساب جیرایی وصل نکردید.',
  'start.not_connected_hint':
    'هنوز وصل نشدید. /connect رو بفرستید تا با هم انجامش بدیم.',
  'start.connect_button': 'اتصال به جیرا',
  'start.help_hint': 'برای دیدن همهٔ کارهایی که می‌شه کرد، /help رو بفرستید.',

  'connect.prompt': 'روی دکمهٔ زیر بزنید و با حساب جیرای خودتون وارد بشید.\n\nرمزتون هیچ‌وقت دست ربات نمی‌افته و ربات هم هیچ‌وقت با حساب کس دیگه‌ای کار نمی‌کنه.',
  'connect.button': 'ورود با جیرا',
  'connect.expires': 'این لینک تا {minutes} دقیقه اعتبار داره.',
  'connect.success': '✅ با حساب <b>{name}</b> وصل شدید.',
  'connect.pick_site': 'به چند تا سایت جیرا دسترسی دارید. با کدومش کار کنیم؟',
  'connect.site_selected': 'سایت فعال: <b>{site}</b>',
  'connect.reauth_needed': '⚠️ اتصال جیراتون منقضی شده. برای ادامه یه بار دیگه وصل بشید.',
  'connect.disconnected': 'اتصال جیرا قطع شد و توکن‌های ذخیره‌شده پاک شدن.',
  'connect.disconnect_confirm': 'اتصال جیرا قطع بشه؟ توکن‌های ذخیره‌شده پاک می‌شن. به اطلاعاتتون تو خود جیرا دست نمی‌خوره.',
  'connect.dc_prompt': 'جیرای شما از نوع Data Center هست و ورود معمولی نداره، به‌جاش یه <b>توکن دسترسی شخصی</b> لازم داریم.\n\nتوکن رو تو یه پیام برام بفرستید. رمزنگاری می‌شه و فقط روی حساب خودتون می‌مونه.\n\nاگه هنوز نساختید، از اینجا بسازید:\n{url}',
  'connect.dc_stored': '✅ با توکن شخصی خودتون به اسم <b>{name}</b> وصل شدید.',

  'home.title': '<b>کارهای من</b>',
  'home.assigned': 'محول‌شده به من',
  'home.reported': 'ثبت‌شده توسط من',
  'home.watched': 'دنبال‌شده',
  'home.due_soon': 'نزدیک موعد',
  'home.overdue': 'گذشته از موعد',
  'home.sprint': 'کارهای اسپرینت من',
  'home.approvals': 'تأییدهای من',
  'home.recent': 'اخیراً دیده‌شده',
  'home.empty': 'فعلاً اینجا خبری نیست.',
  'home.counts': '{assigned} محول‌شده · {overdue} عقب‌افتاده · {dueSoon} نزدیک موعد',

  'issue.status': 'وضعیت',
  'issue.assignee': 'مسئول',
  'issue.reporter': 'ثبت‌کننده',
  'issue.priority': 'اولویت',
  'issue.type': 'نوع',
  'issue.due': 'موعد',
  'issue.updated': 'آخرین تغییر',
  'issue.created': 'ایجاد',
  'issue.sprint': 'اسپرینت',
  'issue.epic': 'اپیک',
  'issue.parent': 'والد',
  'issue.labels': 'برچسب‌ها',
  'issue.resolution': 'نتیجه',
  'issue.time': 'زمان',
  'issue.not_found': 'همچین موردی نیست، یا حساب جیراتون اجازهٔ دیدنش رو نداره.',

  'action.open': 'باز کردن',
  'action.assign': 'واگذاری',
  'action.assign_to_me': 'به خودم',
  'action.unassign': 'حذف مسئول',
  'action.transition': 'تغییر وضعیت',
  'action.comment': 'کامنت',
  'action.log_work': 'ثبت زمان',
  'action.watch': 'دنبال کردن',
  'action.unwatch': 'لغو دنبال',
  'action.link': 'پیوند',
  'action.attach': 'پیوست',
  'action.edit': 'ویرایش',
  'action.delete': 'حذف',
  'action.more': 'بیشتر…',
  'action.vote': 'رأی',
  'action.unvote': 'حذف رأی',
  'action.mute': 'بی‌صدا کردن این مورد',
  'action.unmute': 'لغو بی‌صدا',

  'assign.pick': 'به کی واگذار بشه؟',
  'assign.me': 'خودم',
  'assign.search': 'جستجوی کاربران جیرا',
  'assign.teammates': 'هم‌تیمی‌های متصل',
  'assign.others': 'بقیهٔ کسایی که می‌شه بهشون واگذار کرد',
  'assign.none_assignable': 'جیرا کسی رو برای واگذاری این مورد پیشنهاد نمی‌ده.',
  'assign.done': '✅ {issue} به <b>{name}</b> واگذار شد.',
  'assign.done_unlinked': '✅ {issue} تو جیرا به <b>{name}</b> واگذار شد. تلگرامشون هنوز وصل نیست، برای همین خبری براشون نرفت.',
  'assign.cleared': '✅ {issue} دیگه مسئولی نداره.',
  'assign.not_assignable': 'جیرا اجازه نمی‌ده این مورد به ایشون واگذار بشه.',

  'transition.pick': 'انتقال <b>{issue}</b> به:',
  'transition.none': 'جیرا فعلاً هیچ تغییر وضعیتی برای شما روی این مورد باز نذاشته.',
  'transition.done': '✅ {issue} به <b>{status}</b> رفت.',
  'transition.needs_fields': 'این تغییر وضعیت اطلاعات بیشتری می‌خواد. برنامه رو باز می‌کنیم…',

  'comment.prompt': 'متن کامنت رو برای <b>{issue}</b> بفرستید.',
  'comment.added': '✅ کامنت روی {issue} ثبت شد.',
  'comment.empty': 'متن کامنت نباید خالی باشه.',
  'comment.restricted': 'محدود به {role}',

  'worklog.prompt': 'چقدر روی <b>{issue}</b> وقت گذاشتید؟',
  'worklog.custom': 'دلخواه…',
  'worklog.custom_prompt': 'زمان رو بفرستید، مثلاً <code>1h 30m</code>.',
  'worklog.added': '✅ {time} روی {issue} ثبت شد.',
  'worklog.invalid': 'این مدت‌زمان رو نمی‌شناسم. یه چیزی مثل <code>1h 30m</code> بنویسید.',

  'search.prompt': 'یه عبارت برای جستجو، یا یه کوئری JQL بفرستید.',
  'search.results': '{count} مورد پیدا شد.',
  'search.no_results': 'چیزی پیدا نشد.',
  'search.invalid_jql': 'جیرا این کوئری رو نفهمید.',
  'search.saved': 'با نام <b>{name}</b> ذخیره شد.',

  'create.pick_project': 'کدام پروژه؟',
  'create.pick_type': 'چه نوعی؟',
  'create.summary_prompt': 'یه عنوان کوتاه برای مورد جدید بفرستید.',
  'create.description_prompt': 'توضیح رو بفرستید، یا «رد کردن» رو بزنید.',
  'create.created': '✅ <b>{issue}</b> ساخته شد.',
  'create.missing_fields': 'این نوع مورد فیلدهایی داره که تو چت جا نمی‌شه. برای تکمیلش برنامه رو باز کنید.',

  'sites.title': 'سایت‌های جیرای شما',
  'sites.active': 'فعال',
  'sites.switch': 'رفتن به {site}',
  'sites.switched': 'سایت فعال حالا <b>{site}</b> ـه.',
  'sites.only_one': 'فقط به یه سایت جیرا دسترسی دارید: <b>{site}</b>.',

  'access.title': '<b>دسترسی من</b>',
  'access.identity': 'با حساب <b>{name}</b>{email}',
  'access.site': 'سایت: <b>{site}</b>',
  'access.token_health': 'وضعیت اتصال: {status}',
  'access.scopes': 'دسترسی‌های داده‌شده ({count})',
  'access.projects': 'پروژه‌های در دسترس ({count})',
  'access.roles': 'نقش‌های پروژه',
  'access.refresh': 'به‌روزرسانی دسترسی‌ها',
  'access.refreshed': 'دسترسی‌ها از جیرا به‌روز شد.',
  'access.explain_scopes': 'اینکه اینجا چه کاری ازتون برمیاد رو دو چیز مشخص می‌کنه: اسکوپ‌های OAuth که می‌گن این برنامه اجازه داره چی از جیرا بخواد، و دسترسی‌های خودتون تو جیرا که حرف آخر رو می‌زنن.\n\nتنظیمات ربات فقط می‌تونه محدودترش کنه، هیچ‌وقت بازترش نمی‌کنه.',

  'notify.title': '<b>اعلان‌ها</b>',
  'notify.master': 'همهٔ اعلان‌ها',
  'notify.mode': 'نحوهٔ ارسال',
  'notify.mode_immediate': 'فوری',
  'notify.mode_digest': 'خلاصه',
  'notify.mode_off': 'خاموش',
  'notify.quiet_hours': 'ساعات سکوت',
  'notify.quiet_window': '{start} تا {end} ({tz})',
  'notify.snooze': 'تعویق',
  'notify.snoozed_until': 'تا {time} تعویق خورده.',
  'notify.projects': 'پروژه‌ها',
  'notify.muted_issues': 'موارد بی‌صدا',
  'notify.events': 'رویدادها',
  'notify.locked_by_admin': 'مدیر این تنظیم رو قفل کرده.',
  'notify.saved': 'ذخیره شد.',
  'notify.not_realtime': 'این رویداد آنی نیست. جیرا براش وب‌هوک نمی‌فرسته، برای همین هر چند وقت یه بار چک می‌شه.',
  'notify.mandatory': 'این اعلان از طرف مدیر اجباری شده ({name}) و نمی‌شه خاموشش کرد.',
  'notify.mandatory_title': 'اعلان‌های اجباری',
  'notify.mandatory_hint':
    'مدیر این‌ها رو مستقل از تنظیمات پایین برات می‌فرسته. با این حال فقط چیزهایی رو می‌بینی که تو جیرا هم بهشون دسترسی داری.',
  'notify.mandatory_via_all': 'همه',
  'notify.mandatory_via_team': 'تیم شما',
  'notify.mandatory_via_direct': 'شخص شما',
  'notify.my_teams': 'تیم‌های من',

  'settings.title': '<b>تنظیمات</b>',
  'settings.language': 'زبان',
  'settings.timezone': 'منطقهٔ زمانی',
  'settings.site': 'سایت فعال جیرا',
  'settings.disconnect': 'قطع اتصال جیرا',
  'settings.language_set': 'زبان روی {language} تنظیم شد.',
  'settings.timezone_prompt': 'منطقهٔ زمانی رو بفرستید، مثلاً <code>Asia/Tehran</code>.',
  'settings.timezone_set': 'منطقهٔ زمانی روی {timezone} تنظیم شد.',
  'settings.timezone_invalid': 'این منطقهٔ زمانی رو نمی‌شناسم.',

  'admin.title': '<b>مدیریت</b>',
  'admin.users': 'کاربران',
  'admin.features': 'سیاست قابلیت‌ها',
  'admin.notifications': 'سیاست اعلان‌ها',
  'admin.audit': 'گزارش ممیزی',
  'admin.health': 'سلامت سامانه',
  'admin.no_access': 'شما مدیر این فضای کاری نیستید.',
  'admin.token_never_shown': 'مقدار توکن‌ها هیچ‌وقت اینجا نشون داده نمی‌شه. مدیرها فقط وضعیت اتصال رو می‌بینن.',

  'admin.teams': 'تیم‌ها',
  'admin.teams_hint':
    'تیم یعنی یک اسم برای یک گروه از آدم‌ها. اگه اسم گروه جیرا رو بدی، اعضا از جیرا خونده می‌شن؛ خالی بذاری، لیست همین‌جا دستی می‌مونه.',
  'admin.team_new': 'تیم جدید',
  'admin.team_name': 'نام تیم',
  'admin.team_jira_group': 'گروه جیرا',
  'admin.team_jira_group_none': 'دستی، نه از جیرا',
  'admin.team_sync': 'همگام‌سازی از جیرا',
  'admin.team_synced': 'آخرین همگام‌سازی: {when}',
  'admin.team_never_synced': 'هنوز همگام نشده',
  'admin.team_members': '{count} نفر',
  'admin.team_add_member': 'افزودن نفر',
  'admin.team_sync_note':
    'از هر کس با حساب خودش پرسیده می‌شه که عضو چه گروه‌هایی از جیراست، پس کسی که جیراش رو وصل نکرده باید دستی اضافه بشه.',

  'admin.mandates': 'اعلان‌های اجباری',
  'admin.mandates_hint':
    'اعلان اجباری فرستاده می‌شه، فارغ از این‌که چه کسی چیزی رو خاموش کرده باشه — گیرنده یا خودت — حتی وقتی تغییر رو خودِ طرف داده. فقط دو چیز جلوش رو می‌گیره: نبودن اون محصول، و جیرا: هرکس باز هم فقط چیزی رو می‌بینه که تو جیرا بهش دسترسی داره.',
  'admin.mandate_new': 'اجبار جدید',
  'admin.mandate_name': 'نام',
  'admin.mandate_events': 'اجبار برای این رویدادها',
  'admin.mandate_projects': 'در این پروژه‌ها',
  'admin.mandate_targets': 'برای چه کسانی',
  'admin.mandate_all_members': 'همهٔ اعضای فضای کاری',
  'admin.mandate_teams': 'تیم‌ها',
  'admin.mandate_people': 'افراد مشخص',
  'admin.mandate_bypass_quiet': 'حتی در ساعت‌های سکوتشون',
  'admin.mandate_bypass_quiet_hint': 'پیش‌فرض روشنه. اگه چیزی مهمه ولی می‌تونه تا صبح صبر کنه، خاموشش کن.',
  'admin.mandate_reaches': 'به {count} نفر می‌رسه',
  'admin.mandate_nobody': 'به هیچ‌کس نمی‌رسه — یک هدف انتخاب کن',

  'admin.broadcast': 'ارسال پیام',
  'admin.broadcast_hint':
    'یک پیام سادهٔ تلگرامی می‌فرسته. اعلان جیرا نیست و چیزی از جیرا به کسی نشون نمی‌ده.',
  'admin.broadcast_text': 'متن پیام',
  'admin.broadcast_send': 'ارسال',
  'admin.broadcast_audience': 'ارسال به',
  'admin.broadcast_audience_all': 'همهٔ کسانی که ربات رو استارت کردن',
  'admin.broadcast_audience_selected': 'افرادی که انتخاب می‌کنم',
  'admin.broadcast_audience_team': 'یک تیم',
  'admin.broadcast_history': 'ارسال‌های قبلی',
  'admin.broadcast_queued': 'برای {count} نفر در صف قرار گرفت.',
  'admin.broadcast_progress': '{sent} ارسال‌شده · {failed} ناموفق از {total}',
  'admin.broadcast_cancel': 'توقف ارسال',
  'admin.broadcast_select_all': 'انتخاب همهٔ نمایش‌داده‌شده‌ها',
  'admin.broadcast_clear': 'پاک کردن انتخاب',
  'admin.broadcast_selected': '{count} نفر انتخاب شده',
  'admin.audience_search': 'جست‌وجوی افراد',
  'admin.audience_linked': 'جیرا وصل',
  'admin.audience_not_linked': 'وصل نیست',
  'admin.audience_started': 'شروع: {when}',
  'admin.audience_total': '{count} نفر تا حالا ربات رو استارت کردن',

  'confirm.title': '⚠️ <b>تأیید</b>',
  'confirm.destructive': 'این کار برگشت نداره.',
  'confirm.target': 'هدف: <b>{target}</b>',
  'confirm.effect': 'اثر: {effect}',
  'confirm.expires': 'این تأیید تا {seconds} ثانیه اعتبار داره.',
  'confirm.yes': 'بله، انجام بشه',
  'confirm.expired': 'این تأیید منقضی شده. لطفاً از اول شروع کنید.',
  'confirm.mismatch': 'این تأیید به این عملیات نمی‌خوره.',

  'error.NotConnected': 'اول باید حساب جیراتون رو وصل کنید.',
  'error.ReauthRequired': 'اتصال جیراتون منقضی شده. لطفاً دوباره وصل بشید.',
  'error.SiteNotSelected': 'اول یه سایت جیرا انتخاب کنید.',
  'error.JiraUnauthorized': 'جیرا این نشست رو نپذیرفت. لطفاً دوباره وصل بشید.',
  'error.JiraForbidden': 'جیرا اجازهٔ این کار رو به حساب شما نمی‌ده.',
  'error.JiraNotFound': 'همچین موردی نیست، یا حساب جیراتون اجازهٔ دیدنش رو نداره.',
  'error.JiraValidationError': 'جیرا این درخواست رو نپذیرفت: {detail}',
  'error.JiraRateLimited': 'جیرا شلوغه. چند ثانیهٔ دیگه دوباره امتحان کنید.',
  'error.JiraUnavailable': 'جیرا فعلاً در دسترس نیست. یه کم بعد دوباره تلاش کنید.',
  'error.JiraConflict': 'هم‌زمان با شما یکی دیگه هم اینو تغییر داد. به‌روزرسانی کنید و دوباره امتحان کنید.',
  'error.BotPolicyDenied': 'تنظیمات فضای کاری شما اجازهٔ این کار رو نمی‌ده.',
  'error.FeatureDisabled': 'این قابلیت برای فضای کاری شما خاموشه.',
  'error.InvalidCallback': 'این دکمه دیگه معتبر نیست.',
  'error.ExpiredCallback': 'این دکمه منقضی شده. لطفاً دوباره مورد رو باز کنید.',
  'error.InvalidMiniAppSession': 'نشستتون معتبر نیست. لطفاً برنامه رو دوباره باز کنید.',
  'error.ProviderUnsupported': 'این قابلیت فقط روی Jira Cloud هست.',
  'error.AttachmentTooLarge': 'این فایل خیلی بزرگه.',
  'error.Internal': 'یه مشکلی پیش اومد. لطفاً دوباره تلاش کنید.',
  'error.generic': 'یه مشکلی پیش اومد. لطفاً دوباره تلاش کنید.',
  'error.refresh_access': 'به‌روزرسانی دسترسی',
  'error.reconnect': 'اتصال دوباره به جیرا',
  'error.not_allowlisted': 'این ربات فعلاً آزمایشیه و فقط برای چند نفر مشخص باز شده.',

  'event.issue.assigned_to_me': '📌 <b>{issue}</b> به شما واگذار شد',
  'event.issue.unassigned': '📭 مسئول <b>{issue}</b> حذف شد',
  'event.issue.reassigned': '🔄 <b>{issue}</b> به {to} واگذار شد',
  'event.issue.status_changed': '🔀 <b>{issue}</b> رفت به <b>{to}</b>',
  'event.issue.created': '🆕 <b>{issue}</b> ساخته شد',
  'event.issue.updated': '✏️ <b>{issue}</b> به‌روز شد',
  'event.issue.deleted': '🗑 <b>{issue}</b> حذف شد',
  'event.issue.priority_changed': '❗ اولویت <b>{issue}</b> شد {to}',
  'event.issue.resolution_changed': '✅ <b>{issue}</b> با نتیجهٔ {to} بسته شد',
  'event.issue.due_date_changed': '📅 موعد <b>{issue}</b> شد {to}',
  'event.issue.due_soon': '⏰ موعد <b>{issue}</b> {due} ـه',
  'event.issue.overdue': '🔴 <b>{issue}</b> از {due} عقب افتاده',
  'event.comment.created': '💬 {actor} روی <b>{issue}</b> کامنت گذاشت',
  'event.comment.updated': '💬 {actor} کامنتش رو روی <b>{issue}</b> ویرایش کرد',
  'event.comment.deleted': '💬 کامنتی روی <b>{issue}</b> حذف شد',
  'event.comment.mentioned_me': '📣 {actor} تو <b>{issue}</b> از شما اسم برد',
  'event.attachment.added': '📎 فایلی به <b>{issue}</b> پیوست شد',
  'event.attachment.removed': '📎 فایلی از <b>{issue}</b> حذف شد',
  'event.link.added': '🔗 <b>{issue}</b> پیوند خورد',
  'event.link.removed': '🔗 پیوندی از <b>{issue}</b> حذف شد',
  'event.worklog.added': '⏱ زمانی روی <b>{issue}</b> ثبت شد',
  'event.sprint.started': '🏁 اسپرینت <b>{sprint}</b> شروع شد',
  'event.sprint.closed': '🏁 اسپرینت <b>{sprint}</b> بسته شد',
  'event.issue.added_to_sprint': '🏃 <b>{issue}</b> به {to} اضافه شد',
  'event.jsm.approval_required': '🖊 <b>{issue}</b> منتظر تأیید شماست',
  'event.jsm.sla_warning': '⏳ هشدار SLA روی <b>{issue}</b>',
  'event.jsm.sla_breached': '🚨 نقض SLA روی <b>{issue}</b>',
  'event.jsm.public_comment': '💬 پاسخ جدید روی <b>{issue}</b>',
  'event.digest.title': '<b>خلاصهٔ جیرای شما</b> · {count} به‌روزرسانی',
  'event.by': 'توسط {actor}',

  'help.title': '<b>از من چه کاری برمیاد</b>',
  'help.commands': 'دستورها',
  'help.security': 'هر کاری اینجا با حساب جیرای خودتون انجام می‌شه. چیزی که تو جیرا نمی‌بینید اینجا هم نمی‌بینید، کاری هم که اونجا ازتون برنمیاد اینجا برنمیاد.',
  'issue.overview': 'خلاصه',
  'issue.description': 'توضیحات',
  'issue.no_description': 'برای این ایشو توضیحی نوشته نشده.',
  'issue.components': 'کامپوننت‌ها',
  'issue.fix_versions': 'نسخهٔ رفع',
  'issue.story_points': 'استوری پوینت',
  'issue.subtasks': 'زیرکارها',
  'issue.comments': 'کامنت‌ها',
  'issue.worklogs': 'زمان کارکرد',
  'issue.history': 'تاریخچه',
  'issue.no_comments': 'هنوز کامنتی گذاشته نشده.',
  'issue.no_worklogs': 'هنوز زمانی ثبت نشده.',
  'issue.no_history': 'هنوز تغییری ثبت نشده.',
  'issue.watchers': 'ناظرها',
  'issue.votes': 'رأی‌ها',
  'issue.project': 'پروژه',
  'issue.details': 'جزئیات',
  'home.scope': 'نمایش',
  'home.scope_mine': 'کارهای من',
  'home.scope_all': 'هرچه می‌بینم',
  'home.nothing_assigned': 'الان هیچ کاری به شما ارجاع نشده.',
  'home.nothing_assigned_hint': 'برای دیدن کل سایت «هرچه می‌بینم» را بزنید، یا از پایین یک پروژه انتخاب کنید.',
  'home.browse_projects': 'گشتن در یک پروژه',
  'home.open_issues': 'ایشوهای باز',
  'home.unassigned': 'بدون مسئول',
  'home.all_open': 'همهٔ بازها',
  'home.pick_project': 'پروژه را انتخاب کنید',
  'home.no_projects': 'روی این سایت به هیچ پروژه‌ای دسترسی ندارید.',
  'notify.cadence': 'هر چند وقت',
  'notify.cadence_hourly': 'هر ساعت',
  'notify.cadence_4h': 'هر ۴ ساعت',
  'notify.cadence_daily': 'روزی یک بار',
  'notify.cadence_weekly': 'هفته‌ای یک بار',
  'notify.scope': 'چه چیزی «مال من» حساب شود',
  'notify.scope_hint':
    'این‌ها محدود می‌کنند، اضافه نمی‌کنند: هرکدام را روشن کنی، دیگر از بقیهٔ چیزها خبردار نمی‌شوی. اگر کسی اسمت را ببرد، در هر حال به دستت می‌رسد.',
  'notify.suppress_self': 'کارهای خودم را نفرست',
  'notify.quiet_from': 'از',
  'notify.quiet_to': 'تا',
  'notify.rules': 'قانون‌های من',
  'notify.rules_hint': 'هر قانون وقتی چیزی با آن بخواند به شما خبر می‌دهد. اگر قانونی نسازید، همان حالت پیش‌فرض پایین کار می‌کند.',
  'notify.rule_new': 'قانون تازه',
  'notify.rule_name': 'اسم قانون',
  'notify.rule_events': 'وقتی این اتفاق افتاد',
  'notify.rule_projects': 'در این پروژه‌ها',
  'notify.rule_issue_types': 'برای این نوع‌ ایشوها',
  'notify.rule_statuses': 'وقتی وضعیت این است',
  'notify.rule_assignee': 'و مسئولش این است',
  'notify.rule_any': 'فرقی نمی‌کند',
  'notify.rule_all_projects': 'همهٔ پروژه‌ها',
  'notify.rule_me': 'خودم',
  'notify.rule_group': 'هر کسی در یک تیم',
  'notify.rule_pick_groups': 'کدام تیم‌ها',
  'notify.rule_anyone': 'هر کسی',
  'notify.rule_unassigned': 'هیچ‌کس (بدون مسئول)',
  'notify.rule_delete': 'حذف قانون',
  'notify.rule_saved': 'قانون ذخیره شد.',
  'notify.rule_none': 'هنوز قانونی نساخته‌اید.',
  'notify.rule_stale': 'اگر این‌قدر بی‌تغییر ماند هم خبر بده',
  'notify.rule_stale_off': 'خبر نده',
  'notify.rule_hours': '{hours} ساعت',
  'notify.rule_days': '{days} روز',
  'notify.rule_summary_all': 'همه چیز، همه جا',
  'notify.defaults': 'رویدادهای پیش‌فرض',
  'notify.advanced': 'تنظیمات بیشتر',
  'admin.identity': 'هویت',
  'admin.account': 'حساب',
  'admin.provider': 'نوع سرویس',
  'admin.notifications_allowed': 'اجازهٔ نوتیفیکیشن',
  'admin.lock_user_settings': 'قفل کردن تنظیمات کاربر',
  'admin.permission_snapshots': 'عکس لحظه‌ای دسترسی‌ها',
  'admin.tenant_policy': 'سیاست فضای کاری',
  'admin.notifications_enabled': 'نوتیفیکیشن روشن',
  'admin.webhooks': 'وبهوک‌ها',
  'admin.registrations': 'ثبت‌شده‌ها',
  'admin.backlog': 'صف مانده',
  'admin.dead_letters': 'شکست‌خورده‌ها',
  'admin.suppressed': 'جلوگیری‌شده',
  'admin.queues': 'صف‌ها',
  'admin.needs_reconnect': 'نیازمند اتصال دوباره',
  'agile.title': 'بردها',
  'assets.title': 'دارایی‌ها',
  'assets.object_types': 'نوع اشیا',
  'power.title': 'ابزارهای پیشرفته',
  'common.required': 'الزامی',
  'common.optional': 'اختیاری',
  'common.all': 'همه',
  'common.clear': 'پاک کردن',
  'common.apply': 'اعمال',
  'common.add': 'افزودن',
  'common.fields_count': '{count} فیلد',
  'event.issue.stale': '{issue} به مدت {hours} ساعت بدون تغییر مانده',
  'search.box_label': 'جستجو',
  'search.box_hint': 'کلید ایشو، یا چند کلمه از عنوانش.',
  'search.advanced': 'جستجوی پیشرفته',
  'search.advanced_hint': 'JQL همان زبان کوئری‌ای است که خود جیرا برای فیلترها به کار می‌برد. اگر باهاش کار نکرده‌اید، بسته بگذاریدش.',
  'search.showing_for': 'نتیجه‌ها برای این کوئری',
  'search.edit_query': 'ویرایش این کوئری',
  'agile.board': 'برد',
  'agile.backlog': 'بک‌لاگ',
  'agile.sprints': 'اسپرینت‌ها',
  'agile.epics': 'اپیک‌ها',
  'agile.pick_board': 'یک برد انتخاب کنید',
  'agile.no_boards': 'روی این سایت هیچ بردی نمی‌بینید.',
  'agile.kanban_no_backlog': 'برد کانبان بک‌لاگ جدا ندارد.',
  'agile.kanban_no_sprints': 'برد کانبان اسپرینت ندارد.',
  'agile.board_empty': 'الان هیچ ایشویی روی این برد نیست.',
  'agile.scrum': 'اسکرام',
  'agile.kanban': 'کانبان',
  'agile.no_active_sprint': 'این برد الان اسپرینت فعالی ندارد.',
  'agile.sprint_now': 'اسپرینت فعال',
  'agile.unmapped': 'خارج از ستون‌ها',
  'agile.column_empty': 'خالی',
  'agile.issues_count': '{count} ایشو',
  'sprint.active': 'فعال',
  'sprint.future': 'شروع نشده',
  'sprint.closed': 'بسته‌شده',
  'epic.done': 'انجام‌شده',
  'epic.open': 'باز',
  'agile.no_active_sprint_hint': 'الان هیچ اسپرینتی روی این برد در جریان نیست. اسپرینت‌هایش اینهاست:',
  'agile.sprint_none': 'این برد هنوز اسپرینتی ندارد.',
  'agile.truncated': 'از {total} ایشوی این برد، {shown} تای اول نشان داده شده.',
};

export const CATALOGS: Record<Locale, Messages> = { en, fa };
